import React, { useState } from 'react';
import { acceptTerms } from '../../api/terms';
import styles from './terms-acceptance-modal.module.css';

/**
 * Blocking modal shown when the active customer has not yet accepted
 * the current Terms & Conditions version. Renders as a full-screen
 * overlay that gates the rest of the application until the user
 * clicks Accept (or until they sign out).
 *
 * Props:
 *  - currentVersion (string): the required version
 *  - publicTcUrl (string): public link to the T&C document
 *  - acceptedVersion (string|null): customer's currently-accepted version
 *  - onAccepted (function): called after a successful acceptance
 */
export default function TermsAcceptanceModal({
    currentVersion,
    publicTcUrl,
    acceptedVersion,
    onAccepted,
}) {
    const [checked, setChecked] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const isUpdate = Boolean(acceptedVersion);

    const handleAccept = async () => {
        if (!checked || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            await acceptTerms({ version: currentVersion });
            if (onAccepted) {
                await onAccepted();
            }
        } catch (e) {
            const message = e?.message || 'Could not record acceptance. Please try again.';
            setError(message);
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="tc-modal-heading">
            <div className={styles.modal}>
                <h2 id="tc-modal-heading" className={styles.heading}>
                    {isUpdate ? 'Updated Terms & Conditions' : 'Accept Terms & Conditions'}
                </h2>

                <p className={styles.body}>
                    {isUpdate
                        ? 'Our Terms & Conditions have been updated. Please review and accept the new version to continue using Livisibly.'
                        : 'Before continuing, please review and accept our Terms & Conditions.'}
                </p>

                <p className={styles.versionLine}>
                    Current version: <strong>{currentVersion}</strong>
                    {isUpdate && acceptedVersion ? (
                        <>
                            {' '}— you previously accepted <strong>{acceptedVersion}</strong>.
                        </>
                    ) : null}
                </p>

                <p className={styles.body}>
                    <a
                        className={styles.link}
                        href={publicTcUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Read the Terms & Conditions
                    </a>
                </p>

                <label className={styles.checkboxRow}>
                    <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                        disabled={submitting}
                        aria-describedby="tc-checkbox-label"
                    />
                    <span id="tc-checkbox-label" className={styles.checkboxLabel}>
                        I have read and accept the Livisibly Terms & Conditions (version {currentVersion}).
                    </span>
                </label>

                {error ? <p className={styles.error}>{error}</p> : null}

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.acceptButton}
                        disabled={!checked || submitting}
                        onClick={handleAccept}
                        aria-label="Accept Terms and Conditions"
                    >
                        {submitting ? 'Recording…' : 'Accept'}
                    </button>
                </div>
            </div>
        </div>
    );
}
